import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Empty, Row, Typography } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import type { WorkspaceApplication, WorkspaceRoute, WorkspaceTask } from '../../store/api/workspaceApi';
import {
  ATTENTION_INBOX_CATEGORY_ALL_LABELS,
  ATTENTION_INBOX_CATEGORY_LABELS,
  ATTENTION_INBOX_CATEGORY_LIMIT,
  ATTENTION_INBOX_CATEGORY_ORDER,
  ATTENTION_INBOX_EXPANDED_MAX_HEIGHT,
  buildAttentionInbox,
  readAttentionInboxPersistState,
  writeAttentionInboxPersistState,
  type AttentionDisplayGroup,
  type AttentionDisplayNode,
  type AttentionInboxCategory,
} from '../../domain/workspaceAttentionInbox';
import { AttentionCategoryDrawer } from './AttentionCategoryDrawer';
import {
  AttentionInboxSignalRow,
  ClosureBlockedBadge,
  PRIORITY_COLOR,
  PriorityBadge,
} from './attentionInboxRows';

const { Text } = Typography;

const CATEGORY_ACCENT: Partial<Record<AttentionInboxCategory, string>> = {
  documents: '#d46b08',
  finance: '#722ed1',
  closure: '#1677ff',
};

export interface WorkspaceAttentionInboxProps {
  allTasks: WorkspaceTask[];
  routes: WorkspaceRoute[];
  applications: WorkspaceApplication[];
  roleName: string | null | undefined;
  onOpenFinanceContext: (p: { kind: 'route' | 'application'; id: number }) => void;
  onTaskAction: (task: WorkspaceTask) => void;
}

function GroupRow({
  group,
  expanded,
  applications,
  onToggle,
  onOpenFinanceContext,
  onTaskAction,
}: {
  group: AttentionDisplayGroup;
  expanded: boolean;
  applications: WorkspaceApplication[];
  onToggle: () => void;
  onOpenFinanceContext: (p: { kind: 'route' | 'application'; id: number }) => void;
  onTaskAction: (task: WorkspaceTask) => void;
}) {
  const borderColor = PRIORITY_COLOR[group.groupPriority];

  return (
    <div style={{ borderBottom: '1px solid #f5f5f5' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 6,
          padding: '5px 0 5px 6px',
          borderLeft: `3px solid ${borderColor}`,
          cursor: 'pointer',
        }}
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <span style={{ marginTop: 2, color: '#8c8c8c', fontSize: 10 }}>
          {expanded ? <DownOutlined /> : <RightOutlined />}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontSize: 12, display: 'block' }} ellipsis={{ tooltip: group.entityLabel }}>
            {group.entityLabel}
          </Text>
          <Text style={{ fontSize: 12, display: 'block' }}>{group.title}</Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2, alignItems: 'center' }}>
            <PriorityBadge priority={group.groupPriority} />
            {group.closureBlocked ? <ClosureBlockedBadge /> : null}
          </div>
        </div>
        <Button
          type="link"
          size="small"
          style={{ padding: '0 4px', fontSize: 12, flexShrink: 0 }}
          onClick={(e) => {
            e.stopPropagation();
            onOpenFinanceContext(group.entity);
          }}
        >
          Открыть
        </Button>
      </div>
      {expanded ? (
        <div style={{ paddingLeft: 20, paddingBottom: 4 }}>
          {group.signals.map((signal) => (
            <AttentionInboxSignalRow
              key={signal.id}
              signal={signal}
              applications={applications}
              onOpenFinanceContext={onOpenFinanceContext}
              onTaskAction={onTaskAction}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function DisplayNode({
  node,
  groupsExpanded,
  applications,
  onToggleGroup,
  onOpenFinanceContext,
  onTaskAction,
}: {
  node: AttentionDisplayNode;
  groupsExpanded: Record<string, boolean>;
  applications: WorkspaceApplication[];
  onToggleGroup: (id: string) => void;
  onOpenFinanceContext: (p: { kind: 'route' | 'application'; id: number }) => void;
  onTaskAction: (task: WorkspaceTask) => void;
}) {
  if (node.kind === 'signal') {
    return (
      <AttentionInboxSignalRow
        signal={node.signal}
        closureBlocked={node.closureBlocked}
        applications={applications}
        onOpenFinanceContext={onOpenFinanceContext}
        onTaskAction={onTaskAction}
      />
    );
  }
  return (
    <GroupRow
      group={node}
      expanded={Boolean(groupsExpanded[node.id])}
      applications={applications}
      onToggle={() => onToggleGroup(node.id)}
      onOpenFinanceContext={onOpenFinanceContext}
      onTaskAction={onTaskAction}
    />
  );
}

function CategoryPanel({
  category,
  nodes,
  signalCount,
  groupsExpanded,
  applications,
  onToggleGroup,
  onOpenCategoryDrawer,
  onOpenFinanceContext,
  onTaskAction,
}: {
  category: AttentionInboxCategory;
  nodes: AttentionDisplayNode[];
  signalCount: number;
  groupsExpanded: Record<string, boolean>;
  applications: WorkspaceApplication[];
  onToggleGroup: (id: string) => void;
  onOpenCategoryDrawer: () => void;
  onOpenFinanceContext: (p: { kind: 'route' | 'application'; id: number }) => void;
  onTaskAction: (task: WorkspaceTask) => void;
}) {
  const visible = nodes.slice(0, ATTENTION_INBOX_CATEGORY_LIMIT);
  const showAllLink = signalCount > ATTENTION_INBOX_CATEGORY_LIMIT;
  const accent = CATEGORY_ACCENT[category];

  return (
    <div
      style={{
        border: '1px solid #f0f0f0',
        borderRadius: 8,
        padding: '8px 10px',
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
      }}
    >
      <Text strong style={{ fontSize: 13, color: accent, marginBottom: 6, display: 'block' }}>
        {ATTENTION_INBOX_CATEGORY_LABELS[category]} ({signalCount})
      </Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
        {visible.map((node) => (
          <DisplayNode
            key={node.kind === 'signal' ? node.signal.id : node.id}
            node={node}
            groupsExpanded={groupsExpanded}
            applications={applications}
            onToggleGroup={onToggleGroup}
            onOpenFinanceContext={onOpenFinanceContext}
            onTaskAction={onTaskAction}
          />
        ))}
        {showAllLink ? (
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: 'auto', textAlign: 'left' }}
            onClick={onOpenCategoryDrawer}
          >
            Все {ATTENTION_INBOX_CATEGORY_ALL_LABELS[category]} ({signalCount}) →
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function gridColSpan(activeCount: number): { xs: number; lg: number; xl: number } {
  if (activeCount <= 1) return { xs: 24, lg: 24, xl: 24 };
  if (activeCount === 2) return { xs: 24, lg: 12, xl: 12 };
  if (activeCount === 3) return { xs: 24, lg: 12, xl: 8 };
  return { xs: 24, lg: 12, xl: 6 };
}

export const WorkspaceAttentionInbox: React.FC<WorkspaceAttentionInboxProps> = ({
  allTasks,
  routes,
  applications,
  roleName,
  onOpenFinanceContext,
  onTaskAction,
}) => {
  const inbox = useMemo(
    () => buildAttentionInbox({ allTasks, routes, applications, roleName }),
    [allTasks, routes, applications, roleName]
  );

  const [expanded, setExpanded] = useState(() => readAttentionInboxPersistState().expanded);
  const [groupsExpanded, setGroupsExpanded] = useState(
    () => readAttentionInboxPersistState().groupsExpanded ?? {}
  );
  const [drawerCategory, setDrawerCategory] = useState<AttentionInboxCategory | null>(null);

  const persist = useCallback((nextExpanded: boolean, nextGroups: Record<string, boolean>) => {
    writeAttentionInboxPersistState({
      expanded: nextExpanded,
      groupsExpanded: nextGroups,
    });
  }, []);

  useEffect(() => {
    persist(expanded, groupsExpanded);
  }, [expanded, groupsExpanded, persist]);

  const activeCategories = useMemo(
    () =>
      ATTENTION_INBOX_CATEGORY_ORDER.filter((cat) => {
        if (cat === 'documents') return inbox.counts.documents > 0;
        if (cat === 'finance') return inbox.counts.finance > 0;
        if (cat === 'operations') return inbox.counts.operations > 0;
        return inbox.counts.closure > 0;
      }),
    [inbox.counts]
  );

  const colSpan = gridColSpan(activeCategories.length);

  const toggleGroup = (id: string) => {
    setGroupsExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const signalCountForCategory = (category: AttentionInboxCategory): number => {
    if (category === 'documents') return inbox.counts.documents;
    if (category === 'finance') return inbox.counts.finance;
    if (category === 'operations') return inbox.counts.operations;
    return inbox.counts.closure;
  };

  const { counts } = inbox;
  const titleColor = counts.p0 > 0 ? '#cf1322' : undefined;

  const summaryParts = [
    counts.p0 > 0 ? (
      <Text key="p0" type="secondary">
        P0: <Text strong style={{ color: '#cf1322' }}>{counts.p0}</Text>
      </Text>
    ) : null,
    counts.documents > 0 ? (
      <Text key="doc" type="secondary">
        Документы: <Text strong>{counts.documents}</Text>
      </Text>
    ) : null,
    counts.finance > 0 ? (
      <Text key="fin" type="secondary">
        Финансы: <Text strong>{counts.finance}</Text>
      </Text>
    ) : null,
    counts.operations > 0 ? (
      <Text key="ops" type="secondary">
        Задачи: <Text strong>{counts.operations}</Text>
      </Text>
    ) : null,
    counts.closure > 0 ? (
      <Text key="clo" type="secondary">
        Закрытие: <Text strong>{counts.closure}</Text>
      </Text>
    ) : null,
  ].filter(Boolean);

  return (
    <>
      <Card
        size="small"
        styles={{
          header: {
            position: 'sticky',
            top: 0,
            zIndex: 20,
            background: '#fff',
          },
          body: {
            padding: expanded && counts.total > 0 ? 0 : '8px 12px',
          },
        }}
        title={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <span style={{ color: titleColor, fontWeight: 600 }}>
              Требует внимания ({counts.total})
            </span>
            {summaryParts.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12, fontWeight: 400 }}>
                {summaryParts}
              </div>
            ) : null}
          </div>
        }
        extra={
          <Button type="link" size="small" onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Свернуть' : 'Развернуть'}
          </Button>
        }
      >
        {expanded ? (
          counts.total === 0 ? (
            <div style={{ padding: '8px 12px' }}>
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет срочных действий" style={{ margin: '8px 0' }} />
            </div>
          ) : (
            <div
              style={{
                maxHeight: ATTENTION_INBOX_EXPANDED_MAX_HEIGHT,
                overflowY: 'auto',
                overflowX: 'hidden',
                overscrollBehavior: 'contain',
                padding: '8px 12px',
              }}
            >
              <Row gutter={[10, 10]}>
                {activeCategories.map((category) => (
                  <Col key={category} xs={colSpan.xs} lg={colSpan.lg} xl={colSpan.xl}>
                    <CategoryPanel
                      category={category}
                      nodes={inbox.displayByCategory[category]}
                      signalCount={signalCountForCategory(category)}
                      groupsExpanded={groupsExpanded}
                      applications={applications}
                      onToggleGroup={toggleGroup}
                      onOpenCategoryDrawer={() => setDrawerCategory(category)}
                      onOpenFinanceContext={onOpenFinanceContext}
                      onTaskAction={onTaskAction}
                    />
                  </Col>
                ))}
              </Row>
            </div>
          )
        ) : null}
      </Card>

      <AttentionCategoryDrawer
        open={drawerCategory != null}
        category={drawerCategory}
        signals={drawerCategory ? inbox.byCategory[drawerCategory] : []}
        signalCount={drawerCategory ? signalCountForCategory(drawerCategory) : 0}
        closureBlockedEntities={inbox.closureBlockedEntities}
        routes={routes}
        applications={applications}
        onClose={() => setDrawerCategory(null)}
        onOpenFinanceContext={onOpenFinanceContext}
        onTaskAction={onTaskAction}
      />
    </>
  );
};

export default WorkspaceAttentionInbox;
